import { api, download, fileBase64 } from '../console/api.ts';
import { carrierName, carrierNames } from '../console/carriers.ts';
import { empty, field, fileField, pageHead, pill } from '../console/components.ts';
import { ApiError, explain, InputError } from '../console/errors.ts';
import { cap, date, esc, fmt, icon, type Money, period } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { has, me, supervises } from '../console/session.ts';
import { $, act, qInput, textInput } from '../console/ui.ts';
import { moneyControl, monthControl, selectControl, textControl } from '../controls.ts';

/**
 * «Liquidaciones»: each agent's commissions per period with VAT and ISR (administration prepares,
 * the direction approves, the agent uploads the invoice and the direction records the transfer),
 * and the commission advances.
 */

type SettlementInvoiceView = {
  issuerNit: string;
  receiverNit: string;
  total: Money;
  vat: Money;
  issuedOn: string;
  authorization: string | null;
  xmlFileName: string;
  pdfFileName: string | null;
  submittedOn: string;
  scheduledFor: string;
};
type SettlementView = {
  id: string;
  agentId: string;
  period: string;
  state: string;
  commission: Money;
  recovered: Money;
  base: Money;
  vat: Money;
  isr: Money;
  payout: Money;
  invoiceTotal: Money;
  invoice: SettlementInvoiceView | null;
  paid: { reference: string; on: string } | null;
  advancesRecovered?: Money;
};

type AdvanceOrder = { to: 'ADMINISTRATION' } | { to: 'CARRIER'; carrier: string };
type AdvanceView = {
  id: string;
  agentId: string;
  amount: Money;
  order: AdvanceOrder;
  state: 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'PAID';
  requestedOn: string;
  rejection: string | null;
  paid: { reference: string; on: string } | null;
};
type AdvancePolicyView = { maxShare: { value: string }; order: AdvanceOrder } | null;
type AdvanceAvailability = {
  policy: AdvancePolicyView;
  confirmedShare: Money;
  debt: Money;
  open: Money;
  limit: Money;
};
/** An advance is "el anticipo": its states in the masculine, and what each one means. */
const ADVANCE_STATE: Record<AdvanceView['state'], [string, string]> = {
  REQUESTED: ['PENDING_APPROVAL', 'Por decidir'],
  APPROVED: ['APPROVED', 'Aprobado, por pagar'],
  REJECTED: ['REJECTED', 'Rechazado'],
  PAID: ['PAID', 'Pagado'],
};
const advancePill = (a: AdvanceView) => pill(...ADVANCE_STATE[a.state]);
const orderTo = (o: AdvanceOrder) =>
  o.to === 'CARRIER' ? `Aseguradora ${carrierName(o.carrier)}` : 'Administración';

/** Why the last invoice of a settlement was refused (shown in its card until it is fixed). */
const invoiceProblems = new Map<string, string>();
/** A settlement's state in the words of its cycle (a settlement is feminine: «Pagada»). */
const settlementPill = (s: SettlementView) =>
  s.state === 'PAID'
    ? pill('PAID', 'Pagada')
    : s.state === 'APPROVED' && s.base.amountMinor > 0
      ? pill('APPROVED', 'Espera factura')
      : pill(s.state);

/** The agent's card: what to invoice (base + VAT) and the upload of the FEL's XML and PDF. */
function invoiceUploadCard(s: SettlementView) {
  const id = esc(s.id);
  const problem = invoiceProblems.get(s.id);
  return `<section class="card invoice-card" aria-labelledby="inv-title-${id}">
    <div class="card-head"><span class="monogram lg" aria-hidden="true">${icon('upload')}</span>
      <div class="carrier-name"><h2 id="inv-title-${id}">Sube tu factura de ${esc(period(s.period).toLowerCase())}</h2>
      <small>Tu liquidación fue aprobada. Emite la factura electrónica a nombre de la agencia.</small></div>
      <span class="spacer"></span>${settlementPill(s)}</div>
    <dl class="party-data">
      <div><dt>Base de la factura</dt><dd>${fmt(s.base)}</dd></div>
      <div><dt>IVA</dt><dd>${fmt(s.vat)}</dd></div>
      <div><dt>Total de la factura</dt><dd><strong>${fmt(s.invoiceTotal)}</strong></dd></div>
      <div><dt>Recibirás (menos ISR ${fmt(s.isr)})</dt><dd>${fmt(s.payout)}</dd></div>
    </dl>
    <p class="muted small card-pad">El ISR se retiene del pago; no lo restes de la factura.${s.recovered.amountMinor > 0 ? ` La base ya descuenta ${fmt(s.recovered)} de tu deuda anterior.` : ''} Nubi compara el XML con la liquidación: NIT, total, IVA, fecha y régimen deben coincidir.</p>
    ${problem ? `<p class="note bad invoice-problem" role="alert">${icon('alert')}<span>${esc(problem)}</span></p>` : ''}
    <div class="toolbar invoice-form">
      ${fileField('XML de la factura (FEL)', { id: `inv-xml-${id}`, accept: '.xml,application/xml,text/xml', required: true, hint: 'XML · hasta 200 KB' })}
      ${fileField('PDF de la factura (opcional)', { id: `inv-pdf-${id}`, accept: '.pdf,application/pdf', hint: 'PDF · hasta 2 MB' })}
      <button type="button" class="btn primary" data-invoice-send="${id}">Validar y enviar</button>
    </div></section>`;
}

/** The accepted invoice: what it said, its files, and (for the director) the transfer. */
function invoiceCard(s: SettlementView) {
  const i = s.invoice;
  if (!i) return '';
  const id = esc(s.id);
  const who = cap(s.agentId);
  const pay =
    s.state === 'SCHEDULED' && has('agency_director')
      ? `<div class="toolbar invoice-form">
          ${field('Referencia de la transferencia', textControl({ id: `payout-ref-${s.id}`, placeholder: 'BI-0000000', autocomplete: 'off' }))}
          <button type="button" class="btn primary" data-payout="${id}">Registrar transferencia de ${fmt(s.payout)}</button></div>`
      : '';
  return `<section class="card invoice-card" aria-labelledby="inv-title-${id}">
    <div class="card-head"><span class="monogram lg" aria-hidden="true">${icon('settle')}</span>
      <div class="carrier-name"><h2 id="inv-title-${id}">Factura de ${esc(who)} · ${esc(period(s.period))}</h2>
      <small>${s.state === 'PAID' ? `Transferencia ${esc(s.paid?.reference ?? '')} del ${esc(date(s.paid?.on))}` : `Transferencia estimada: ${esc(date(i.scheduledFor))}`}</small></div>
      <span class="spacer"></span>${settlementPill(s)}</div>
    <dl class="party-data">
      <div><dt>Total de la factura</dt><dd>${fmt(i.total)}</dd></div>
      <div><dt>IVA</dt><dd>${fmt(i.vat)}</dd></div>
      <div><dt>A transferir (menos ISR ${fmt(s.isr)})</dt><dd><strong>${fmt(s.payout)}</strong></dd></div>
      <div><dt>NIT del emisor</dt><dd class="mono">${esc(i.issuerNit)}</dd></div>
      <div><dt>NIT del receptor</dt><dd class="mono">${esc(i.receiverNit)}</dd></div>
      <div><dt>Fecha de emisión</dt><dd>${esc(date(i.issuedOn))}</dd></div>
      <div><dt>Autorización</dt><dd class="mono">${esc(i.authorization ?? '—')}</dd></div>
    </dl>
    <div class="toolbar invoice-files"><span class="grow muted small">Coincide con la liquidación. Subida el ${esc(date(i.submittedOn))}.</span>
      <button type="button" class="btn sm" data-invoice-file="${id}" data-kind="xml">${icon('receipt')}XML</button>
      ${i.pdfFileName ? `<button type="button" class="btn sm" data-invoice-file="${id}" data-kind="pdf">${icon('receipt')}PDF</button>` : ''}
    </div>${pay}</section>`;
}

/**
 * Commission advances: the agent sees what they may ask for and asks; the director sets the
 * agency's rule and decides and pays; administration follows them.
 */
async function anticipos(): Promise<string> {
  const isAgent = has('agent') && !!me?.agentId;
  const manages = supervises();
  if (!isAgent && !manages) return '';
  const [advances, availability, policy] = await Promise.all([
    api<AdvanceView[]>('GET', '/v1/commission-advances'),
    isAgent
      ? api<AdvanceAvailability>('GET', '/v1/commission-advances/availability')
      : Promise.resolve(null),
    manages
      ? api<{ policy: AdvancePolicyView }>('GET', '/v1/advance-policy').then((r) => r.policy)
      : Promise.resolve(null),
  ]);
  let html = '';
  if (availability) {
    const p = availability.policy;
    html += `<section class="card" aria-labelledby="adv-title"><div class="card-head"><span class="monogram lg" aria-hidden="true">${icon('cash')}</span>
      <div class="carrier-name"><h2 id="adv-title">Anticipo de comisiones</h2><small>${p ? `Hasta el ${esc(p.maxShare.value)} % de tus comisiones confirmadas por liquidar; la orden va a ${esc(orderTo(p.order).toLowerCase())}.` : 'Tu agencia no tiene anticipos habilitados.'}</small></div></div>
      <div class="kpis advance-kpis" data-component="advance-kpis">
        <div class="kpi"><span class="kpi-label">Comisiones confirmadas por liquidar</span><span class="kpi-value">${fmt(availability.confirmedShare)}</span><span class="kpi-foot">tu parte, antes de IVA e ISR</span></div>
        <div class="kpi"><span class="kpi-label">Anticipos abiertos y deuda</span><span class="kpi-value">${fmt({ amountMinor: availability.open.amountMinor + availability.debt.amountMinor, currency: 'GTQ' })}</span><span class="kpi-foot">se descuentan de tu siguiente liquidación</span></div>
        <div class="kpi"><span class="kpi-label">Puedes pedir hasta</span><span class="kpi-value">${fmt(availability.limit)}</span><span class="kpi-foot">${p ? 'la dirección lo aprueba o rechaza' : 'pídele a dirección que los habilite'}</span></div>
      </div>
      ${
        p && availability.limit.amountMinor > 0
          ? `<div class="toolbar"><div class="grow"><strong>Pedir un anticipo</strong><span class="muted small">Se descuenta del pago de tu siguiente liquidación.</span></div>
        ${field('Monto', moneyControl({ id: 'adv-amount' }))}
        <button type="button" class="btn primary" id="adv-request" data-adv-request>Pedir anticipo</button></div>`
          : ''
      }</section>`;
  }
  if (has('agency_director')) {
    const carriers = Object.keys(carrierNames);
    const to = policy?.order.to === 'CARRIER' ? policy.order.carrier : '';
    html += `<section class="card toolbar" aria-label="Regla de anticipos">
      <div class="grow"><strong>Anticipos de comisiones</strong><span class="muted small">${policy ? `Hoy: hasta el ${esc(policy.maxShare.value)} %, órdenes a ${esc(orderTo(policy.order).toLowerCase())}.` : 'Hoy las agentes no pueden pedir anticipos.'}</span></div>
      ${field('Hasta (%)', textControl({ id: 'adv-share', inputmode: 'decimal', value: policy?.maxShare.value ?? '50' }))}
      ${field('La orden va a', selectControl({ id: 'adv-order' }, [{ value: '', label: 'Administración' }, ...carriers.map((c) => ({ value: c, label: `Aseguradora ${carrierName(c)}`, selected: c === to }))]))}
      <button type="button" class="btn" id="adv-policy" data-adv-policy>Guardar regla</button></section>`;
  }
  if (advances.length) {
    const director = has('agency_director');
    html += `<section class="card"><div class="card-head"><h2>Anticipos</h2></div><div class="table-wrap" data-component="table"><table class="table"><thead><tr>${manages ? '<th>Agente</th>' : ''}<th>Pedido</th><th class="num">Monto</th><th>Orden a</th><th>Estado</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
      ${advances
        .map((a) => {
          let action = '';
          if (director && a.state === 'REQUESTED')
            action = `<span class="row-actions"><button type="button" class="btn sm primary" data-adv-approve="${esc(a.id)}">Aprobar</button>
              ${textControl({ 'data-adv-reason': a.id, label: 'Motivo del rechazo', hideLabel: true, placeholder: 'Motivo del rechazo' })}
              <button type="button" class="btn sm danger" data-adv-reject="${esc(a.id)}">Rechazar</button></span>`;
          else if (director && a.state === 'APPROVED')
            action = `<span class="row-actions">${textControl({ 'data-adv-ref': a.id, label: 'Referencia del pago', hideLabel: true, placeholder: 'BI-0000000' })}
              <button type="button" class="btn sm primary" data-adv-pay="${esc(a.id)}">Registrar pago de ${fmt(a.amount)}</button></span>`;
          else if (a.state === 'REJECTED' && a.rejection)
            action = `<span class="muted small">${esc(a.rejection)}</span>`;
          else if (a.paid)
            action = `<span class="muted small mono">${esc(a.paid.reference)}</span>`;
          return `<tr>${manages ? `<td>${esc(cap(a.agentId))}</td>` : ''}<td>${date(a.requestedOn)}</td><td class="num">${fmt(a.amount)}</td>
            <td>${esc(orderTo(a.order))}</td><td>${advancePill(a)}</td><td class="actions">${action}</td></tr>`;
        })
        .join('')}</tbody></table></div></section>`;
  }
  return html;
}

export async function liquidaciones() {
  const [list, agents, advances] = await Promise.all([
    api<SettlementView[]>('GET', '/v1/settlements'),
    api<{ agentId: string }[]>('GET', '/v1/agents').catch(() => [] as { agentId: string }[]),
    anticipos(),
  ]);
  const current = new Date().toISOString().slice(0, 7);
  let html = pageHead(
    'Liquidaciones',
    has('agent') && !has('agency_admin', 'agency_director')
      ? 'Tus comisiones por período, con IVA e ISR. Cuando dirección aprueba una, sube aquí tu factura electrónica.'
      : 'Comisiones de cada agente por período, con IVA e ISR calculados. Administración prepara; dirección aprueba; el agente sube su factura y dirección registra la transferencia.',
  );
  if (has('agency_admin'))
    html += `<section class="card toolbar" aria-label="Preparar liquidación">
      <div class="grow"><strong>Preparar liquidación</strong><span class="muted small">Toma las comisiones conciliadas del período.</span></div>
      ${field(
        'Agente',
        selectControl(
          { id: 'set-agent' },
          agents.map((a) => ({ value: a.agentId, label: cap(a.agentId) })),
        ),
      )}
      ${field('Período', monthControl({ id: 'set-period', value: current }))}
      <button type="button" class="btn primary" id="prepare" data-settlement-prepare>Preparar</button></section>`;
  // Newest first: what needs doing is on top.
  const recent = [...list].reverse();
  const awaiting = has('agent')
    ? recent.filter((s) => s.state === 'APPROVED' && s.base.amountMinor > 0)
    : [];
  const invoiced = recent.filter((s) => s.invoice && s.state === 'SCHEDULED');
  html += advances;
  html += awaiting.map(invoiceUploadCard).join('') + invoiced.map(invoiceCard).join('');
  html += list.length
    ? `<section class="card"><div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Agente</th><th>Período</th><th class="num">Comisión</th><th class="num">IVA</th><th class="num">Factura</th><th class="num">ISR retenido</th><th class="num">Anticipos</th><th class="num">A pagar</th><th>Estado</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
      ${list
        .map(
          (
            s,
          ) => `<tr><td><span class="carrier-cell"><span class="monogram" aria-hidden="true">${esc(s.agentId.slice(0, 2).toUpperCase())}</span>${esc(cap(s.agentId))}</span></td>
          <td>${esc(period(s.period))}</td><td class="num">${fmt(s.commission)}</td>
          <td class="num">${fmt(s.vat)}</td><td class="num">${fmt(s.invoiceTotal)}</td><td class="num">${fmt(s.isr)}</td><td class="num">${s.advancesRecovered?.amountMinor ? `−${fmt(s.advancesRecovered)}` : '—'}</td><td class="num"><strong>${fmt(s.payout)}</strong></td>
          <td>${settlementPill(s)}</td>
          <td class="actions">${s.state === 'PREPARED' && has('agency_director') ? `<button type="button" class="btn sm primary" data-approve="${esc(s.id)}">Aprobar</button>` : ''}${
            s.invoice && s.state === 'PAID'
              ? `<button type="button" class="btn sm" data-invoice-file="${esc(s.id)}" data-kind="xml" aria-label="Descargar el XML de la factura de ${esc(cap(s.agentId))}, ${esc(period(s.period))}">${icon('receipt')}XML</button>`
              : ''
          }</td></tr>`,
        )
        .join('')}
      </tbody></table></div></section>`
    : empty(
        'settle',
        'No hay liquidaciones todavía',
        'Cuando administración prepare la primera, aparecerá aquí para que dirección la apruebe.',
        '',
        2,
      );
  return html;
}

/** Forgets why an invoice was refused (a new session). */
export function resetSettlements() {
  invoiceProblems.clear();
}

export const actions: Actions = {
  click: {
    advRequest: (t) => {
      void act(t, 'Pidiendo el anticipo…', async () => {
        const amount = qInput('#adv-amount');
        if (amount.amountMinor <= 0)
          throw new InputError('Escribe cuánto necesitas, por ejemplo 500.00');
        const a = await api<AdvanceView>('POST', '/v1/commission-advances', { amount });
        await render(current, { quiet: true });
        return `Anticipo de ${fmt(a.amount)} pedido. La orden va a ${orderTo(a.order).toLowerCase()}; falta que dirección lo apruebe.`;
      });
    },
    advPolicy: (t) => {
      void act(t, 'Guardando la regla de anticipos…', async () => {
        const maxShare = textInput('#adv-share', 'el porcentaje').replace('%', '').trim();
        const carrier = ($('#adv-order') as HTMLSelectElement).value;
        await api('PUT', '/v1/advance-policy', {
          maxShare,
          order: carrier ? { to: 'CARRIER', carrier } : { to: 'ADMINISTRATION' },
        });
        await render(current, { quiet: true });
        return `Regla guardada: las agentes pueden pedir hasta el ${maxShare} % de sus comisiones confirmadas.`;
      });
    },
    advApprove: (t, d) => {
      void act(t, 'Aprobando el anticipo…', async () => {
        const a = await api<AdvanceView>(
          'POST',
          `/v1/commission-advances/${encodeURIComponent(d.advApprove ?? '')}/approve`,
        );
        await render(current, { quiet: true });
        return `Anticipo de ${fmt(a.amount)} para ${cap(a.agentId)} aprobado. Falta registrar el pago.`;
      });
    },
    advReject: (t, d) => {
      const id = d.advReject;
      if (!id) return;
      void act(t, 'Rechazando el anticipo…', async () => {
        const reason = textInput(`[data-adv-reason="${CSS.escape(id)}"]`, 'el motivo del rechazo');
        await api('POST', `/v1/commission-advances/${encodeURIComponent(id)}/reject`, { reason });
        await render(current, { quiet: true });
        return 'Anticipo rechazado; la agente verá el motivo.';
      });
    },
    advPay: (t, d) => {
      const id = d.advPay;
      if (!id) return;
      void act(t, 'Registrando el pago…', async () => {
        const bankReference = textInput(
          `[data-adv-ref="${CSS.escape(id)}"]`,
          'la referencia del pago',
        );
        const a = await api<AdvanceView>(
          'POST',
          `/v1/commission-advances/${encodeURIComponent(id)}/payout`,
          { bankReference },
        );
        await render(current, { quiet: true });
        return `Pago de ${fmt(a.amount)} registrado; se descontará de la siguiente liquidación de ${cap(a.agentId)}.`;
      });
    },
    settlementPrepare: (t) => {
      void act(t, 'Preparando la liquidación…', async () => {
        await api('POST', '/v1/settlements', {
          agentId: ($('#set-agent') as HTMLSelectElement).value,
          period: ($('#set-period') as HTMLInputElement).value,
        });
        await render(current, { quiet: true });
        return 'Liquidación preparada. Falta la aprobación de dirección.';
      });
    },
    invoiceSend: (t, d) => {
      const id = d.invoiceSend;
      if (!id) return;
      void act(t, 'Validando la factura con la liquidación…', async () => {
        const xml = (document.getElementById(`inv-xml-${id}`) as HTMLInputElement | null)
          ?.files?.[0];
        const pdf = (document.getElementById(`inv-pdf-${id}`) as HTMLInputElement | null)
          ?.files?.[0];
        if (!xml) throw new InputError('Elige el XML de tu factura electrónica.');
        if (!/\.xml$/i.test(xml.name))
          throw new InputError('La factura debe ser el archivo .xml del FEL.');
        if (xml.size > 200_000) throw new InputError('El XML pesa más de 200 KB.');
        if (pdf && !/\.pdf$/i.test(pdf.name))
          throw new InputError('El PDF debe ser un archivo .pdf.');
        if (pdf && pdf.size > 2 * 1024 * 1024) throw new InputError('El PDF pesa más de 2 MB.');
        try {
          const s = await api<SettlementView>(
            'POST',
            `/v1/settlements/${encodeURIComponent(id)}/invoice`,
            {
              xml: { fileName: xml.name, content: await xml.text() },
              ...(pdf ? { pdf: { fileName: pdf.name, base64: await fileBase64(pdf) } } : {}),
            },
          );
          invoiceProblems.delete(id);
          await render(current, { quiet: true });
          return `Factura aceptada: coincide con la liquidación. Transferencia programada para el ${date(s.invoice?.scheduledFor)}.`;
        } catch (e) {
          if (
            e instanceof ApiError &&
            (e.code === 'INVOICE_MISMATCH' || e.code === 'INVOICE_UNREADABLE')
          ) {
            invoiceProblems.set(id, explain(e).human);
            await render(current, { quiet: true });
          }
          throw e;
        }
      });
    },
    invoiceFile: (t, d) => {
      if (!d.invoiceFile) return;
      const path = `/v1/settlements/${encodeURIComponent(d.invoiceFile)}/invoice/${d.kind === 'pdf' ? 'pdf' : 'xml'}`;
      void act(t, 'Descargando…', async () => `«${await download(path)}» descargado.`);
    },
    payout: (t, d) => {
      const id = d.payout;
      if (!id) return;
      void act(t, 'Registrando la transferencia…', async () => {
        const ref = (
          document.getElementById(`payout-ref-${id}`) as HTMLInputElement | null
        )?.value.trim();
        if (!ref) throw new InputError('Escribe la referencia de la transferencia.');
        await api('POST', `/v1/settlements/${encodeURIComponent(id)}/payout`, {
          bankReference: ref,
        });
        await render(current, { quiet: true });
        return 'Transferencia registrada: la liquidación quedó pagada.';
      });
    },
    approve: (t, d) => {
      void act(t, 'Aprobando…', async () => {
        await api('POST', `/v1/settlements/${encodeURIComponent(d.approve ?? '')}/approve`);
        await render(current, { quiet: true });
        return 'Liquidación aprobada. El agente recibe el aviso para subir su factura.';
      });
    },
  },
};
